// The authoring kit of a feature file (ADR-0027 D9).
//
// A feature file is `src/features/<module>/<feature>.feature.tsx`. It holds
// the routes, the sidebar rows and the slot items of ONE feature id, and its
// default export is `defineWebFeature({ id: '<feature id>', ... })` with the
// id as the first property. The Vite plugin reads the id from the source and
// imports the file only when the Feature Manifest enables the id, so the
// bundle never holds the pages of a feature that is off.
//
// Keep a feature file small: it is in the entry chunk. A page is a `lazy`
// import, and a slot component is a `lazy` import of a module beside the
// file, so their code loads with the page that shows them.
import {
    defineWebFeature as defineCoreWebFeature,
    type NavContribution,
    type RouteContribution,
    type WebFeature,
} from '@septima/feature-core/react';
import type { StaffShellKey } from '@septima/contract';
// The Dowgate catalogue makes `FeatureId` the union of the known ids.
import type { DowgateFeatureId } from '@septima/dowgate-features';

/** A feature id of this deployment's catalogue. */
export type AppFeatureId = DowgateFeatureId;

/** The layouts that hold a route: a staff shell, the portal, or the root (`public`). */
export type RouteLayout = StaffShellKey | 'portal' | 'public';

export interface AppRoute extends RouteContribution {
    readonly frontend: RouteLayout;
}

export interface AppNav extends NavContribution {
    readonly frontend: StaffShellKey;
}

export interface AppWebFeature extends WebFeature {
    readonly routes?: readonly AppRoute[];
    readonly nav?: readonly AppNav[];
}

/** Declares the contributions of one feature, with the layout and view keys of this app. */
export function defineWebFeature<const F extends AppWebFeature>(feature: F): F {
    return defineCoreWebFeature(feature);
}
